import React, { useState } from 'react';
import { MdDelete } from 'react-icons/md';
import applicationService from '../services/applicationService';

const DepartureSection = ({ application, userRole, onRefresh }) => {
  const [feedback, setFeedback] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showOnHomepage, setShowOnHomepage] = useState(application?.departure?.showOnHomepage || false);
  const [updatingHomepage, setUpdatingHomepage] = useState(false);
  const [showDepartedModal, setShowDepartedModal] = useState(false);
  const [showNotDepartedModal, setShowNotDepartedModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [remark, setRemark] = useState('');
  const [newTicketFile, setNewTicketFile] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const isCandidate = userRole === 'CANDIDATE';
  const isAdmin = ['ADMIN', 'CHAIRMAN'].includes(userRole);
  const departure = application?.departure || {};
  const hasSubmitted = Boolean(departure?.videoUrl && departure?.feedback);

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only MP4, MOV, and WEBM video files are allowed');
      e.target.value = '';
      return;
    }

    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Video size must be less than 25MB');
      e.target.value = '';
      return;
    }

    setVideoFile(file);
  };

  const handleDeleteVideo = async () => {
    if (!window.confirm('Are you sure you want to delete the departure video? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      const response = await applicationService.deleteDepartureVideo(application._id);
      if (response.success) {
        alert('Departure video deleted successfully');
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete departure video');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleHomepage = async (e) => {
    const newValue = e.target.checked;
    setUpdatingHomepage(true);
    try {
      const response = await applicationService.updateDepartureHomepageStatus(application._id, newValue);
      if (response.success) {
        setShowOnHomepage(newValue);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update homepage status');
      e.target.checked = !newValue;
    } finally {
      setUpdatingHomepage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!videoFile) {
      alert('Please upload your departure video');
      return;
    }

    if (!feedback || feedback.trim().length < 5) {
      alert('Please enter feedback (minimum 5 characters)');
      return;
    }

    setSubmitting(true);
    try {
      const response = await applicationService.submitDeparture(application._id, {
        feedback: feedback.trim(),
        file: videoFile
      });

      if (response.success) {
        alert('Departure video and feedback submitted successfully. Application remains in DEPARTURE stage.');
        setFeedback('');
        setVideoFile(null);
        const fileInput = document.getElementById('departure-video-input');
        if (fileInput) fileInput.value = '';
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit departure details');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeparted = async () => {
    if (!remark || remark.trim().length < 10) {
      alert('Please provide a remark (minimum 10 characters)');
      return;
    }

    setActionLoading(true);
    try {
      const response = await applicationService.markDeparted(application._id, remark.trim());
      if (response.success) {
        alert('Application marked as departed and moved to Completed');
        setShowDepartedModal(false);
        setRemark('');
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark as departed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleNotDeparted = async () => {
    if (!remark || remark.trim().length < 10) {
      alert('Please provide a remark (minimum 10 characters)');
      return;
    }

    setActionLoading(true);
    try {
      const response = await applicationService.markNotDeparted(application._id, remark.trim());
      if (response.success) {
        alert('Application marked as not departed with remark');
        setShowNotDepartedModal(false);
        setRemark('');
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark as not departed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRescheduleTicket = async () => {
    if (!remark || remark.trim().length < 10) {
      alert('Please provide a remark (minimum 10 characters)');
      return;
    }

    if (!newTicketFile) {
      alert('Please upload the new ticket file');
      return;
    }

    setActionLoading(true);
    try {
      const response = await applicationService.rescheduleTicket(application._id, {
        remark: remark.trim(),
        file: newTicketFile
      });
      if (response.success) {
        alert('Ticket rescheduled successfully. Candidate will be notified via SMS.');
        setShowRescheduleModal(false);
        setRemark('');
        setNewTicketFile(null);
        const fileInput = document.getElementById('reschedule-ticket-input');
        if (fileInput) fileInput.value = '';
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reschedule ticket');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <h3 className="m-0 mb-3 text-base font-semibold text-primary pb-1 border-b border-accent-green">Departure Section</h3>

      {/* Not Departed Reason Display */}
      {departure?.notDepartedRemark && isAdmin && (
        <div className="bg-[#ffebee] border-2 border-red-500 rounded-md p-4 mb-3">
          <div className="flex items-start gap-2 mb-2">
            <div className="w-3 h-3 bg-red-500 rounded-full mt-1 flex-shrink-0"></div>
            <div className="flex-1">
              <p className="m-0 text-base font-bold text-red-700 mb-2">⚠️ NOT DEPARTED</p>
              <p className="m-0 text-sm text-red-600 mb-2">
                <strong>Reason:</strong> {departure.notDepartedRemark}
              </p>
              <div className="text-xs text-red-500">
                {departure.notDepartedAt && (
                  <span>Marked on {new Date(departure.notDepartedAt).toLocaleDateString()} at {new Date(departure.notDepartedAt).toLocaleTimeString()}</span>
                )}
                {departure.notDepartedBy && (
                  <span className="ml-2">by User ID: {departure.notDepartedBy}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {hasSubmitted && (
        <div className="bg-[#e8f5e9] border border-[#c8e6c9] rounded-md p-3 mb-3">
          <div className="flex items-start justify-between gap-3 mb-2">
            <p className="m-0 text-sm text-text-primary font-medium">Departure details submitted.</p>
            {isAdmin && (
              <button
                onClick={handleDeleteVideo}
                disabled={deleting}
                className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white rounded text-xs font-semibold transition-all duration-200 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete departure video"
              >
                <MdDelete size={16} />
                {deleting ? 'Deleting...' : 'Delete Video'}
              </button>
            )}
          </div>
          <p className="m-0 mb-2 text-sm text-text-primary">
            <strong>Feedback:</strong> {departure.feedback}
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <a
              href={departure.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
            >
              View Uploaded Video
            </a>
            {isAdmin && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOnHomepage}
                  onChange={handleToggleHomepage}
                  disabled={updatingHomepage}
                  className="w-4 h-4 cursor-pointer"
                />
                <span className={updatingHomepage ? 'opacity-50' : ''}>
                  Show feedback on homepage
                </span>
              </label>
            )}
          </div>
        </div>
      )}

      {isCandidate && application?.currentStage === 'DEPARTURE' && !hasSubmitted && (
        <form onSubmit={handleSubmit} className="bg-[#f8f9fa] border border-[#dee2e6] rounded-md p-3">
          <div className="flex flex-col gap-3">
            <div className="bg-[#fff3cd] text-[#856404] p-3 rounded border border-[#ffeeba] text-sm">
              Upload one 5-second departure video and enter feedback. Application will remain in DEPARTURE stage until HR confirms your departure.
            </div>

            <label htmlFor="departure-video-input" className="font-medium text-[#495057] text-[0.9rem]">
              5-Second Video
            </label>
            <input
              id="departure-video-input"
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              onChange={handleVideoChange}
              disabled={submitting}
              className="p-2.5 border border-[#ced4da] rounded text-[0.95rem] bg-white"
            />

            <label htmlFor="departure-feedback" className="font-medium text-[#495057] text-[0.9rem]">
              Feedback
            </label>
            <textarea
              id="departure-feedback"
              rows={4}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              minLength={5}
              maxLength={1000}
              disabled={submitting}
              placeholder="Share your departure feedback"
              className="w-full p-3 border border-[#ced4da] rounded text-[0.95rem] bg-white"
            />

            <button
              type="submit"
              className="py-1.5 px-3 rounded-md font-semibold text-[13px] leading-8 h-fit min-h-0 cursor-pointer transition-all duration-200 border-none whitespace-nowrap inline-flex items-center justify-center align-middle box-border bg-accent-green text-white hover:bg-[#5a6b4d] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Departure Details'}
            </button>

            <small className="text-[#6c757d] text-[0.85rem]">Allowed video types: MP4, MOV, WEBM. Max size: 25MB.</small>
          </div>
        </form>
      )}

      {!isCandidate && !hasSubmitted && (
        <div className="bg-[#e7f3ff] text-[#004085] p-3 rounded border border-[#b8daff] text-[0.9rem]">
          Candidate must upload a 5-second video and feedback in Departure stage. Use the "Departed" button below to mark as completed.
        </div>
      )}

      {/* HR/Admin Action Buttons */}
      {isAdmin && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="m-0 mb-3 text-sm font-semibold text-gray-700">
            Departure Actions
            <span className="ml-2 text-xs text-gray-500">
              (Stage: {application?.currentStage})
            </span>
          </h4>
          {(application?.currentStage === 'DEPARTURE' || application?.currentStage === 'COMPLETED') ? (
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => setShowDepartedModal(true)}
                className="py-2 px-4 bg-green-600 text-white rounded-md text-sm font-semibold transition-all duration-200 hover:bg-green-700"
              >
                Departed
              </button>
              <button
                onClick={() => setShowNotDepartedModal(true)}
                className="py-2 px-4 bg-red-600 text-white rounded-md text-sm font-semibold transition-all duration-200 hover:bg-red-700"
              >
                Not Departed
              </button>
              <button
                onClick={() => setShowRescheduleModal(true)}
                className="py-2 px-4 bg-orange-600 text-white rounded-md text-sm font-semibold transition-all duration-200 hover:bg-orange-700"
              >
                Reschedule Ticket
              </button>
            </div>
          ) : (
            <div className="text-sm text-gray-600 bg-yellow-50 p-3 rounded border border-yellow-200">
              These actions are only available when the application is in DEPARTURE stage.
              Current stage: <strong>{application?.currentStage}</strong>
            </div>
          )}
        </div>
      )}

      {/* Departed Modal */}
      {showDepartedModal && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={() => setShowDepartedModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="m-0 mb-2 text-xl font-bold text-green-600">Confirm Departure</h3>
            <p className="m-0 mb-4 text-sm text-gray-600">
              Marking this application as departed will move it to Completed status. Please provide a remark.
            </p>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter remark (minimum 10 characters)"
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-vertical mb-2"
            />
            <div className="text-xs text-gray-500 mb-4">{remark.length}/500 characters</div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDepartedModal(false);
                  setRemark('');
                }}
                disabled={actionLoading}
                className="py-2 px-4 bg-gray-100 text-gray-700 rounded-md text-sm font-semibold hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeparted}
                disabled={actionLoading || remark.length < 10}
                className="py-2 px-4 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Processing...' : 'Confirm Departed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Not Departed Modal */}
      {showNotDepartedModal && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={() => setShowNotDepartedModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="m-0 mb-2 text-xl font-bold text-red-600">Mark as Not Departed</h3>
            <p className="m-0 mb-4 text-sm text-gray-600">
              Please provide a reason why the candidate did not depart. The application will remain incomplete.
            </p>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter reason (minimum 10 characters)"
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-vertical mb-2"
            />
            <div className="text-xs text-gray-500 mb-4">{remark.length}/500 characters</div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowNotDepartedModal(false);
                  setRemark('');
                }}
                disabled={actionLoading}
                className="py-2 px-4 bg-gray-100 text-gray-700 rounded-md text-sm font-semibold hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleNotDeparted}
                disabled={actionLoading || remark.length < 10}
                className="py-2 px-4 bg-red-600 text-white rounded-md text-sm font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Processing...' : 'Confirm Not Departed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Ticket Modal */}
      {showRescheduleModal && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={() => setShowRescheduleModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="m-0 mb-2 text-xl font-bold text-orange-600">Reschedule Ticket</h3>
            <p className="m-0 mb-4 text-sm text-gray-600">
              Upload the new ticket and provide a remark about the rescheduling.
            </p>
            <div className="mb-4">
              <label htmlFor="reschedule-ticket-input" className="block text-sm font-medium text-gray-700 mb-2">New Ticket File</label>
              <input
                id="reschedule-ticket-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setNewTicketFile(e.target.files?.[0] || null)}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
              />
              <small className="text-xs text-gray-500">Allowed: PDF, JPG, PNG. Max size: 10MB</small>
            </div>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter remark about rescheduling (minimum 10 characters)"
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-vertical mb-2"
            />
            <div className="text-xs text-gray-500 mb-4">{remark.length}/500 characters</div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRescheduleModal(false);
                  setRemark('');
                  setNewTicketFile(null);
                }}
                disabled={actionLoading}
                className="py-2 px-4 bg-gray-100 text-gray-700 rounded-md text-sm font-semibold hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRescheduleTicket}
                disabled={actionLoading || remark.length < 10 || !newTicketFile}
                className="py-2 px-4 bg-orange-600 text-white rounded-md text-sm font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Processing...' : 'Confirm Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartureSection;
